import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import classnames from 'classnames';

import { getMilestoneData, IMilestone } from './get-milestone-data';
import { ReactComponent as MilestoneBadgeSVG } from './img/milestone-badge.svg';
import milestoneCardBackground from './img/milestone-card-bg.png';
import { IWorkoutsCompleted } from '.';
import findLastIndex from 'lodash.findlastindex';
import Icon from '@ifit/react-icons/dist/components/icon';
import CHECK_CIRCLE_LINE from '@ifit/react-icons/dist/icons/CHECK_CIRCLE_LINE';
import { white } from '@ifit/base-coat/js/colors';
import { milestonesLineDivider } from '../../../../i18n/translations';

const MilestoneBadge = ({
  isRewardMilestone,
  workoutsToComplete,
}: IMilestone) => (
  <div
    className={classnames('milestone-badge', {
      'is-reward': isRewardMilestone,
    })}
  >
    <MilestoneBadgeSVG />
    <h1>{workoutsToComplete}</h1>
  </div>
);

const MilestoneCard = React.forwardRef<
  HTMLDivElement,
  IMilestone & IWorkoutsCompleted & { isMobile: boolean }
>(({ workoutsCompleted, isMobile, ...milestone }, ref) => {
  const completed = milestone.workoutsToComplete <= workoutsCompleted;
  return (
    <div
      className={classnames('milestone-card', {
        completed: completed,
      })}
      style={{ backgroundImage: `url(${milestoneCardBackground})` }}
      ref={ref}
    >
      {completed && (
        <div className="completed-overlay">
          <Icon
            icon={CHECK_CIRCLE_LINE}
            size={isMobile ? 60 : 113}
            color={white}
          />
        </div>
      )}
      <MilestoneBadge {...milestone} />
      <div className="milestone-text">
        <span>
          {completed
            ? milestone.milestoneCompletedText
            : milestone.milestoneText}
        </span>
      </div>
    </div>
  );
});
MilestoneCard.displayName = 'MilestoneCard';

const MilestonesCards = ({
  workoutsCompleted,
  isMobile,
}: IWorkoutsCompleted & { isMobile: boolean }) => {
  const milestoneData = useMemo(getMilestoneData, []);
  const containerRef = React.useRef<HTMLDivElement>();
  const endSpacerRef = useRef<HTMLDivElement>();
  const cardRefs = useRef(
    Array.from({ length: milestoneData.length }).map(() =>
      React.createRef<HTMLDivElement>(),
    ),
  );
  const lastCompletedIndex = useMemo(
    () =>
      findLastIndex(
        milestoneData,
        milestone => milestone.workoutsToComplete <= workoutsCompleted,
      ),
    [milestoneData, workoutsCompleted],
  );
  const completedAll = lastCompletedIndex === milestoneData.length - 1;
  const currentCardRef = cardRefs.current[lastCompletedIndex];
  const scrollToCurrentCard = useCallback(() => {
    const { offsetLeft } = currentCardRef.current;
    const scrollTo = offsetLeft - 30; // to account for gradient / space between cards
    const spaceNeeded = Math.max(
      0,
      containerRef.current.offsetWidth +
        scrollTo -
        containerRef.current.scrollWidth,
    );
    if (spaceNeeded && !completedAll) {
      endSpacerRef.current.style.flex = `0 0 ${spaceNeeded}px`;
    }
    containerRef.current.scrollLeft = scrollTo;
  }, [currentCardRef, containerRef, completedAll]);
  useEffect(() => {
    if (!isMobile && currentCardRef && currentCardRef.current) {
      scrollToCurrentCard();
    }
  }, [scrollToCurrentCard, isMobile, currentCardRef]);

  const toRender = milestoneData.map((milestone, index) => (
    <MilestoneCard
      {...milestone}
      key={`milestone${milestone.workoutsToComplete}`}
      workoutsCompleted={workoutsCompleted}
      ref={cardRefs.current[index]}
      isMobile={isMobile}
    />
  ));

  if (lastCompletedIndex > -1 && !completedAll) {
    const futureMilestonesDivider = (
      <div className="vertical-divide end-spacing" key="vertical-divide">
        <div className="line"></div>
        <div className="word">{milestonesLineDivider()}</div>
      </div>
    );
    toRender.splice(lastCompletedIndex + 1, 0, futureMilestonesDivider);
  }

  const cards = (
    <div className="milestone-cards" ref={containerRef}>
      {toRender}
      <div data-testid="spacerRef" ref={endSpacerRef} />
    </div>
  );

  return isMobile ? (
    cards
  ) : (
    <div className="container">
      {cards}
      <div className="gradient" />
    </div>
  );
};

export default MilestonesCards;
